// src/pages/PrivacyRequests.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { useUserRole } from '../hooks/useUserRole';
import { 
  PrivacyRequest, 
  REQUEST_TYPES, 
  PrivacyRequestType 
} from '../types/privacyRequest';
import { 
  createPrivacyRequest, 
  getUserPrivacyRequests,
  getUserPrivacyPreferences,
  updateUserPrivacyPreferences
} from '../lib/privacy/privacyRequestApi';
import { aggregateUserData } from '../lib/privacy/dataAggregation';
import { exportUserData } from '../lib/privacy/dataExport';
import { UserPrivacyPreferences } from '../types/privacyRequest';

const PrivacyRequests: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [preferences, setPreferences] = useState<UserPrivacyPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New request form state
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [selectedRequestType, setSelectedRequestType] = useState<PrivacyRequestType | ''>('');
  const [requestDescription, setRequestDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Export state
  const [exportingData, setExportingData] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState<'json' | 'csv'>('json');
  
  useEffect(() => {
    if (!roleLoading && (!user || role !== 'client')) {
      navigate('/client-dashboard');
    }
  }, [roleLoading, user, role, navigate]);
  
  const loadData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError('');
      
      const [userRequests, userPrefs] = await Promise.all([
        getUserPrivacyRequests(user.uid),
        getUserPrivacyPreferences(user.uid)
      ]);
      
      setRequests(userRequests);
      setPreferences(userPrefs);
    } catch (err: any) {
      console.error('Error loading privacy data:', err);
      setError('Failed to load privacy requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);
  
  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedRequestType) return;
    
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      // For opt-out and limit requests, update preferences directly
      if (selectedRequestType === 'opt-out') {
        await updateUserPrivacyPreferences(user.uid, {
          optOutSale: true,
          optOutSharing: true,
          optOutMarketing: true
        });
        setSuccess('Your opt-out preferences have been saved.');
        setShowNewRequestForm(false);
        await loadData();
        return;
      }
      
      if (selectedRequestType === 'limit') {
        await updateUserPrivacyPreferences(user.uid, {
          limitSensitiveInfo: true
        });
        setSuccess('Your request to limit sensitive information has been saved.');
        setShowNewRequestForm(false);
        await loadData();
        return;
      }
      
      // For other requests, create a privacy request
      await createPrivacyRequest(
        user.uid,
        user.email || '',
        selectedRequestType,
        requestDescription || undefined
      );
      
      setSuccess('Your privacy request has been submitted successfully. We will process it within 45 days.');
      setShowNewRequestForm(false);
      setSelectedRequestType('');
      setRequestDescription('');
      await loadData();
    } catch (err: any) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleExportData = async (requestId: string) => {
    if (!user) return;
    
    try {
      setExportingData(true);
      setError('');
      
      const userData = await aggregateUserData(user.uid, user.email || '', requestId);
      exportUserData(userData, selectedExportFormat);
      
      setSuccess('Your data has been exported successfully.');
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError('Failed to export data: ' + err.message);
    } finally {
      setExportingData(false);
    }
  };
  
  const getStatusColor = (status: PrivacyRequest['status']) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'in_progress':
        return '#ffc107';
      case 'rejected':
        return '#dc3545';
      case 'expired':
        return '#6c757d';
      default:
        return '#007bff';
    }
  };
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'N/A';
    }
  };
  
  const getDaysUntilDeadline = (deadline: any) => {
    if (!deadline) return null;
    try {
      const deadlineDate = deadline.toDate ? deadline.toDate() : new Date(deadline);
      const now = new Date();
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };
  
  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }
  
  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>Consumer Privacy Rights</h1>
        <p style={{ marginBottom: '2rem', color: '#666' }}>
          Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), 
          you have the right to know, delete, correct, export, and opt-out of certain uses of your 
          personal information.
        </p>
        
        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {success}
          </div>
        )}
        
        {/* Privacy Preferences Section */}
        {preferences && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '2rem'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Your Privacy Preferences</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <strong>Opt-Out of Sale:</strong> {preferences.optOutSale ? '✓ Yes' : '✗ No'}
              </div>
              <div>
                <strong>Opt-Out of Sharing:</strong> {preferences.optOutSharing ? '✓ Yes' : '✗ No'}
              </div>
              <div>
                <strong>Opt-Out of Marketing:</strong> {preferences.optOutMarketing ? '✓ Yes' : '✗ No'}
              </div>
              <div>
                <strong>Limit Sensitive Info:</strong> {preferences.limitSensitiveInfo ? '✓ Yes' : '✗ No'}
              </div>
            </div>
          </div>
        )}
        
        {/* New Request Button */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => setShowNewRequestForm(!showNewRequestForm)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {showNewRequestForm ? 'Cancel' : '+ Submit New Request'}
          </button>
        </div>
        
        {/* New Request Form */}
        {showNewRequestForm && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            marginBottom: '2rem'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Submit Privacy Request</h2>
            <form onSubmit={handleSubmitRequest}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Request Type *
                </label>
                <select
                  value={selectedRequestType}
                  onChange={(e) => setSelectedRequestType(e.target.value as PrivacyRequestType)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select a request type...</option>
                  {REQUEST_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.title}
                    </option>
                  ))}
                </select>
                {selectedRequestType && (
                  <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.875rem' }}>
                    {REQUEST_TYPES.find(t => t.id === selectedRequestType)?.description}
                  </p>
                )}
              </div>
              
              {(selectedRequestType === 'correct' || selectedRequestType === 'know' || selectedRequestType === 'delete') && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Additional Details (Optional)
                  </label>
                  <textarea
                    value={requestDescription}
                    onChange={(e) => setRequestDescription(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      fontSize: '1rem',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Provide any additional details that would help us process your request..."
                  />
                </div>
              )}
              
              <button
                type="submit"
                disabled={submitting || !selectedRequestType}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: submitting ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        )}
        
        {/* Existing Requests */}
        <div>
          <h2 style={{ marginBottom: '1rem' }}>Your Privacy Requests</h2>
          {requests.length === 0 ? (
            <p style={{ color: '#666' }}>You haven't submitted any privacy requests yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {requests.map((request) => {
                const requestType = REQUEST_TYPES.find(t => t.id === request.requestType);
                const daysUntilDeadline = getDaysUntilDeadline(request.deadline);
                const isOverdue = daysUntilDeadline !== null && daysUntilDeadline < 0;
                
                return (
                  <div
                    key={request.id}
                    style={{
                      padding: '1.5rem',
                      backgroundColor: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>{requestType?.title || request.requestType}</h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
                          Submitted: {formatDate(request.submittedAt)}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px',
                            backgroundColor: getStatusColor(request.status),
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}
                        >
                          {request.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {request.deadline && (
                          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: isOverdue ? '#dc3545' : '#666' }}>
                            {isOverdue ? 'Overdue' : `Due in ${daysUntilDeadline} days`}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {request.description && (
                      <p style={{ marginBottom: '1rem', color: '#495057' }}>{request.description}</p>
                    )}
                    
                    {request.status === 'completed' && request.requestType === 'portability' && request.id && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                        <p style={{ margin: 0, marginBottom: '0.5rem' }}>Export your data:</p>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <select
                            value={selectedExportFormat}
                            onChange={(e) => setSelectedExportFormat(e.target.value as 'json' | 'csv')}
                            style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                          >
                            <option value="json">JSON</option>
                            <option value="csv">CSV</option>
                          </select>
                          <button
                            onClick={() => request.id && handleExportData(request.id)}
                            disabled={exportingData}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: exportingData ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {exportingData ? 'Exporting...' : 'Export Data'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {request.deletionExceptions && request.deletionExceptions.length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                        <strong>Deletion Notes:</strong>
                        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                          {request.deletionExceptions.map((exception, idx) => (
                            <li key={idx} style={{ fontSize: '0.875rem' }}>{exception}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {request.notes && request.notes.length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <strong>Internal Notes:</strong>
                        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                          {request.notes.map((note, idx) => (
                            <li key={idx} style={{ fontSize: '0.875rem' }}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Information Section */}
        <div style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>About Your Privacy Rights</h3>
          <p style={{ marginBottom: '1rem' }}>
            We will respond to your request within 45 days. For complex requests, we may extend 
            this period by an additional 45 days with notice. We may need to verify your identity 
            before processing certain requests.
          </p>
          <p style={{ margin: 0 }}>
            For more information, please review our{' '}
            <a href="/privacy-policy" style={{ color: '#007bff', textDecoration: 'none' }}>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyRequests;
